import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import styled from "@mui/material/styles/styled";
import { QualifiedFeature } from "fe-common/graphql";
import {
  DRUG_OR_MEDICAMENT,
  FREE_TEXT_FEATURE_ID,
  KahunCategories,
  Relations,
} from "shared";
import {
  SearchComponent,
  UserEnterSymptomsMode,
} from "../../search/searchComponent";
import { useTranslate } from "../../../hooks/useTranslate";
import { FeatureWithName, SearchOptions } from "fe-web-components/autocomplete";
import {
  buildFreeTextQualifier,
  convertFreeTextQF,
} from "fe-common/logic/qualifiedFeature/freeText";
import { PatientTextField } from "../../uiControllers/patientTextField";
import { RecursivePartial } from "fe-common/commonTypes/types";
import { featureToLines } from "fe-common/logic";
import { PatientFacingQuestion } from "../../../types/patientConversationAliasing";
import { MedicationFrequencySelector } from "./medicationFrequencySelector";
import { AnswerChip } from "../../../style/chips";
import { NumericQuestionLayout } from "../rangeQuestions/numericQuestions/numericQuestionLayout";
import { commonBoxShadowStyle } from "fe-common/ui/boxShadow";
import { symptomTxt } from "../../uiControllers/SymptomText";
//region [[ Styles ]]
const MedicationSelectionView = styled("div")({});

const MedicationSearch = styled(SearchComponent)({
  marginTop: "10px",
  marginBottom: "10px",
  "& .list-container": {
    background: "white",
  },
  "& .search-view": {
    background: "white",
    ...commonBoxShadowStyle,
  },
  "& .ak-feature-selection-input": {
    padding: "5px 23px",
  },
});

const DosageTextField = styled(PatientTextField)({
  "& fieldset": {
    borderRadius: "9px",
  },
  "& input": {
    padding: "9px",
  },
  marginBottom: "10px",
});

const MedicationNameChip = styled(AnswerChip)({
  "& .patient-MuiChip-label": {
    textTransform: "capitalize",
  },
});
//endregion [[ Styles ]]
//region [[ Props ]]
export interface MedicationSelectionProps {
  onAddMedication: (qf: QualifiedFeature) => void;
  // After selecting a disease we ask for its dosage and instruction,
  // use this event the make the parent aware that we are in mid-process
  onStartedDescribingMedicationInfo?: (inProgress: boolean) => void;
  question: PatientFacingQuestion;
  // Is this the second medication to be added? if so we can change the place holder text
  isSecond?: boolean;
}
//endregion [[ Props ]]
//region [[ Functions ]]
//endregion [[ Functions ]]

const panelSearchOptionsMedications: SearchOptions = {
  panelIds: null,
  patientFacing: true,
  categories: [KahunCategories.MEDICINE],
  ancestorsIds: [DRUG_OR_MEDICAMENT],
};

export const MedicationSelectionForm = ({
  onAddMedication,
  onStartedDescribingMedicationInfo,
  question,
  ...props
}: MedicationSelectionProps) => {
  const { translate } = useTranslate();

  const [medication, setMedication] = useState<QualifiedFeature>(null);

  const [dosage, setDosage] = useState<string>("");

  const [direction, setDirection] = useState<QualifiedFeature>(null);

  const [showDirection, setShowDirection] = useState(false);

  const currentRef = useRef<HTMLDivElement>(null);

  const handleDosageChange = (event) => {
    const value = event.target.value;
    setDosage(value);
  };

  const initializeForm = useCallback(() => {
    setMedication(null);
    setDosage("");
    setDirection(null);
    setShowDirection(false);
    onStartedDescribingMedicationInfo?.(false);
  }, [
    setMedication,
    setDosage,
    setDirection,
    onStartedDescribingMedicationInfo,
  ]);

  const handleMedicationSelected = useCallback(
    (event: React.ChangeEvent<{}>, value: FeatureWithName | null) => {
      let selectedQf = value.value;

      selectedQf =
        selectedQf.feature.id === FREE_TEXT_FEATURE_ID
          ? (convertFreeTextQF(selectedQf, {
              featureId: DRUG_OR_MEDICAMENT,
              relationId: Relations.EXPOSURE_TO,
            }) as QualifiedFeature)
          : selectedQf;

      setMedication(selectedQf);

      onStartedDescribingMedicationInfo?.(true);
    },
    [onStartedDescribingMedicationInfo],
  );

  const onAdd = useCallback(
    (_direction?: QualifiedFeature) => {
      if (!_direction && !direction) {
        // We show the add button also to let the user enter the direction
        setShowDirection(true);
        return;
      }
      // Otherwise send the selected medication structure to parent
      const qf: RecursivePartial<QualifiedFeature> = {
        ...medication,
        qualifiers: [
          ...medication.qualifiers,
          buildFreeTextQualifier(dosage, Relations.DOSAGE),
          ...(_direction || direction)?.qualifiers,
        ],
      };

      onAddMedication(qf as QualifiedFeature);

      initializeForm();
    },
    [medication, dosage, onAddMedication, direction, initializeForm],
  );

  const handleDosageKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        onAdd();
      }
    },
    [onAdd],
  );

  const medicationName = !medication
    ? ""
    : featureToLines(medication, false, [
        Relations.EXPOSURE_TO,
      ])[0].toLowerCase();

  useEffect(() => {
    if (currentRef.current) {
      currentRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "end",
      });
    }
  }, [medication, showDirection]);

  const handleFrequencySelected = (e) => {
    setDirection(e);
    onAdd(e);
  };

  const medicationUpdatedText =
    medicationName +
    (showDirection && dosage && dosage !== "" ? `(${dosage})` : "") +
    (direction
      ? symptomTxt(direction)
          .join("")
          .replace("Drug", "")
      : "");

  return (
    <MedicationSelectionView>
      {!medication && (
        <MedicationSearch
          ref={currentRef}
          onChange={handleMedicationSelected}
          placeholder={translate(
            props.isSecond
              ? "AUTO_COMPLETE_MEDICATION_SECOND"
              : "AUTO_COMPLETE_MEDICATION",
          )}
          searchOptions={panelSearchOptionsMedications}
          autoFocus
          noIcon
          userEnterSymptom={UserEnterSymptomsMode.FREE_TEXT}
          enterSymptomTextOverride={{
            titleText: "ENTER_ALLERGY_DESCRIPTION",
            cantFindText: "CANT_FIND_MEDICATION",
            placeholderText: props.isSecond // Used by analytics to identify the event and as the ph in the free text box
              ? "AUTO_COMPLETE_MEDICATION_SECOND"
              : "AUTO_COMPLETE_MEDICATION",
          }}
        />
      )}
      {medication && (
        <>
          <MedicationNameChip
            className={"answer-chip"}
            label={medicationUpdatedText}
          />
          <NumericQuestionLayout
            questionText={`${translate(
              "MEDICATION_REFINEMENT_QUESTION",
            ).replace("#MEDICATION#", medicationName)}`}
            onSubmitClick={() => onAdd()}
            onSkipClicked={() => setShowDirection(true)}
            isAnswered={showDirection}
            textAsQuestion={false}
            ref={currentRef}
          >
            <DosageTextField
              value={dosage || ""}
              onBlur={handleDosageChange}
              onChange={handleDosageChange}
              onKeyDown={handleDosageKeyDown}
              variant="outlined"
              style={{ width: "100%" }}
              id="outlined-basic"
              placeholder={translate("DOSAGE")}
              autoFocus
            />
          </NumericQuestionLayout>
        </>
      )}
      {showDirection && (
        <NumericQuestionLayout
          questionText={`${translate("MEDICATION_DIRECTION").replace(
            "#MEDICATION#",
            medicationName,
          )}`}
          onSubmitClick={onAdd}
          submitDisabled={!direction}
          onSkipClicked={() => onAdd({ qualifiers: [] } as QualifiedFeature)}
          isAnswered={false}
          textAsQuestion={false}
          preventSubmit // we don't want to show submit as selecting an item will case answering the question
          ref={currentRef}
        >
          <MedicationFrequencySelector
            onFrequencySelected={handleFrequencySelected}
            suggestions={question.suggestions?.[2].refinements}
            medicationName={medicationName}
          />
        </NumericQuestionLayout>
      )}
    </MedicationSelectionView>
  );
};
